import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { formatDate, formatDateTime, formatRelativeTime, formatCurrency, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Shield, Users, Building2, Settings, Search, Plus, ChevronRight, Activity,
  AlertTriangle, CheckCircle, Clock, Eye, Edit, Ban, UserCheck, KeyRound,
  Flag, Loader2, LogOut, BarChart3, FileText, DollarSign, Database, Zap,
  ExternalLink, RefreshCw, Mail, UserPlus, CreditCard, TrendingUp, Send,
  Briefcase, Globe, Lock, Unlock, Play, Pause, X, AlertCircle, Home,
  PieChart, LineChart, LayoutDashboard, Ticket, Key, ChevronDown, Copy, User, Save, Book, PenLine,
  Trash2, ToggleRight, Link2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell
} from 'recharts';

// Admin API wrapper
const adminAPI = {
  login: (data) => api.post('/admin/auth/login', data),
  me: () => api.get('/admin/auth/me'),
  listPlatformUsers: () => api.get('/admin/auth/users'),
  createPlatformUser: (data) => api.post('/admin/auth/create-user', data),
  
  listTenants: (params) => api.get('/admin/tenants', { params }),
  getTenant: (id) => api.get(`/admin/tenants/${id}`),
  updateTenantStatus: (id, data) => api.put(`/admin/tenants/${id}/status`, data),
  extendTrial: (id, data) => api.put(`/admin/tenants/${id}/extend-trial`, data),
  inviteTenant: (data) => api.post('/admin/tenants/invite', data),
  updateTenantPlan: (id, data) => api.put(`/admin/tenants/${id}/plan`, data),
  
  listUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  disableUser: (id, data) => api.put(`/admin/users/${id}/disable`, data),
  enableUser: (id) => api.put(`/admin/users/${id}/enable`),
  forceLogout: (id, data) => api.post(`/admin/users/${id}/force-logout`, data),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  
  listFeatureFlags: (params) => api.get('/admin/feature-flags', { params }),
  updateFeatureFlag: (tenantId, data) => api.put(`/admin/feature-flags/${tenantId}`, data),
  
  listAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  
  getSystemStats: () => api.get('/admin/system/stats'),
  getMetrics: () => api.get('/admin/metrics'),
  listSystemJobs: (params) => api.get('/admin/system/jobs', { params }),
  
  listSupportAccess: (params) => api.get('/admin/support-access', { params }),
  createSupportAccess: (data) => api.post('/admin/support-access', data),
  revokeSupportAccess: (id, data) => api.delete(`/admin/support-access/${id}`, { data }),
  
  listSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  compSubscription: (id, data) => api.put(`/admin/subscriptions/${id}/comp`, data),
  applyCredit: (id, data) => api.post(`/admin/subscriptions/${id}/credit`, data),
  
  // Impersonation
  startImpersonation: (data) => api.post('/admin/impersonate/start', data),
  endImpersonation: () => api.post('/admin/impersonate/end'),
  upgradeImpersonation: (data) => api.post('/admin/impersonate/upgrade-to-write', data),
  listImpersonationSessions: (params) => api.get('/admin/impersonate/sessions', { params }),
  
  // Platform OAuth settings
  getOAuthCredentials: () => api.get('/admin/oauth-credentials'),
  createOAuthCredential: (data) => api.post('/admin/oauth-credentials', data),
  updateOAuthCredentials: (provider, data) => api.post('/admin/oauth-credentials', { provider, ...data }),
  deleteOAuthCredential: (provider) => api.delete(`/admin/oauth-credentials/${provider}`),
  
  // Pricing & Plans
  getPricing: () => api.get('/admin/pricing'),
  updatePricing: (data) => api.put('/admin/pricing', data),
  
  // Subscription Tiers Management
  getSubscriptionTiers: () => api.get('/admin/subscription-tiers'),
  updateSubscriptionTiers: (tiers) => api.put('/admin/subscription-tiers', { tiers }),
  assignTenantPlan: (tenantId, data) => api.post(`/admin/tenants/${tenantId}/subscription/assign-plan`, data),
  grantTenantTrial: (tenantId, data) => api.post(`/admin/tenants/${tenantId}/subscription/grant-trial`, data),
  
  // Promo Codes
  listPromoCodes: (params) => api.get('/admin/promo-codes', { params }),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  updatePromoCode: (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromoCode: (id) => api.delete(`/admin/promo-codes/${id}`),
  validatePromoCode: (data) => api.post('/admin/promo-codes/validate', data),
  
  // Roles & Permissions
  getRoles: () => api.get('/admin/roles'),
  updateRoles: (data) => api.put('/admin/roles', data),
  getPermissions: () => api.get('/admin/roles/permissions'),
  
  // Subscription Management
  changeTenantPlan: (id, data) => api.post(`/admin/tenants/${id}/subscription/change-plan`, data),
  extendTenantTrial: (id, data) => api.post(`/admin/tenants/${id}/subscription/extend-trial`, data),
  applyTenantCredit: (id, data) => api.post(`/admin/tenants/${id}/subscription/apply-credit`, data),
  createCompAccount: (id, data) => api.post(`/admin/tenants/${id}/subscription/comp`, data),
  pauseSubscription: (id, data) => api.post(`/admin/tenants/${id}/subscription/pause`, data),
  resumeSubscription: (id) => api.post(`/admin/tenants/${id}/subscription/resume`),
  
  // System health
  getSystemHealth: () => api.get('/admin/system/health'),
  retryJob: (jobId) => api.post(`/admin/system/jobs/${jobId}/retry`),
  listWebhookLogs: (params) => api.get('/admin/system/webhook-logs', { params }),
  
  // Platform settings
  getPlatformSettings: () => api.get('/admin/platform/settings'),
  updatePlatformSettings: (data) => api.put('/admin/platform/settings', data),
  
  // Email Connections Management
  listEmailConnections: () => api.get('/admin/email-connections'),
  deleteEmailConnection: (provider, firmId, userId = null) => {
    const params = userId ? { user_id: userId } : {};
    return api.delete(`/admin/email-connections/${provider}/${firmId}`, { params });
  },
  getEmailConnectionStats: () => api.get('/admin/email-connections/stats'),
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const getStatusBadge = (status) => {
  const styles = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    trial: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    professional: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    enterprise: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    past_due: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
    cancelled: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    disabled: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
  };
  return styles[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
};

const PLATFORM_ROLES = {
  platform_owner: { label: 'Platform Owner', color: 'bg-amber-500/20 text-amber-400', description: 'Full access to everything' },
  platform_admin: { label: 'Platform Admin', color: 'bg-purple-500/20 text-purple-400', description: 'Manage tenants, users, feature flags' },
  support_agent: { label: 'Support Agent', color: 'bg-blue-500/20 text-blue-400', description: 'Troubleshoot tenants, view logs' },
  billing_support: { label: 'Billing Support', color: 'bg-emerald-500/20 text-emerald-400', description: 'Manage subscriptions, credits' },
  engineer: { label: 'Engineer', color: 'bg-slate-500/20 text-slate-400', description: 'System logs, feature flags' },
};

// Sidebar Navigation Component
const Sidebar = ({ activeSection, setActiveSection, platformUser }) => {
  const navSections = [
    {
      title: null,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      ]
    },
    {
      title: 'Tenant Management',
      items: [
        { id: 'tenants', label: 'Tenants', icon: Building2 },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
      ]
    },
    {
      title: 'Platform Config',
      items: [
        { id: 'billing-config', label: 'Billing & Pricing', icon: DollarSign },
        { id: 'roles', label: 'Roles & Access', icon: Lock },
        { id: 'feature-flags', label: 'Feature Flags', icon: Flag },
        { id: 'oauth-settings', label: 'Integrations', icon: Key },
      ]
    },
    {
      title: 'Operations',
      items: [
        { id: 'platform-team', label: 'Platform Team', icon: Shield },
        { id: 'system-health', label: 'System Health', icon: Activity },
        { id: 'audit-logs', label: 'Audit Logs', icon: FileText },
      ]
    },
    {
      title: null,
      items: [
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'documentation', label: 'Documentation', icon: Book, isLink: true, href: '/admin/docs' },
      ]
    }
  ];

  return (
    <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col h-screen fixed left-0 top-0">
      {/* Logo */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-white">LEAP Pro</h1>
            <p className="text-xs text-slate-500">Admin Console</p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-3">
          {navSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className={section.title ? 'mt-4 first:mt-0' : ''}>
              {section.title && (
                <p className="px-3 mb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  {section.title}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  
                  if (item.isLink) {
                    return (
                      <a
                        key={item.id}
                        href={item.href}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all text-slate-400 hover:text-white hover:bg-slate-800"
                        data-testid={`nav-${item.id}`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                        <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                      </a>
                    );
                  }
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isActive 
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                      data-testid={`nav-${item.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
      
      {/* User Info */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-blue-600 text-white text-sm">
              {platformUser?.name?.charAt(0) || 'A'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{platformUser?.name}</p>
            <p className="text-xs text-slate-500 truncate">{PLATFORM_ROLES[platformUser?.role]?.label}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Section with Charts
const DashboardSection = ({ stats, metrics, loading }) => {
  // Mock trend data for charts
  const tenantTrend = metrics?.tenant_trend || [
    { month: 'Jul', tenants: 2 }, { month: 'Aug', tenants: 3 }, { month: 'Sep', tenants: 4 },
    { month: 'Oct', tenants: 5 }, { month: 'Nov', tenants: 6 }, { month: 'Dec', tenants: 7 }
  ];
  
  const usageTrend = metrics?.usage_trend || [
    { month: 'Jul', cases: 5, docs: 12 }, { month: 'Aug', cases: 8, docs: 20 },
    { month: 'Sep', cases: 12, docs: 35 }, { month: 'Oct', cases: 15, docs: 48 },
    { month: 'Nov', cases: 18, docs: 60 }, { month: 'Dec', cases: 20, docs: 75 }
  ];

  const planDistribution = [
    { name: 'Trial', value: stats?.tenants?.trial || 0, color: '#f59e0b', gradient: 'from-amber-500 to-orange-500' },
    { name: 'Professional', value: stats?.tenants?.professional || 2, color: '#3b82f6', gradient: 'from-blue-500 to-cyan-500' },
    { name: 'Expert', value: stats?.tenants?.expert || 0, color: '#8b5cf6', gradient: 'from-purple-500 to-pink-500' },
    { name: 'Enterprise', value: stats?.tenants?.enterprise || 1, color: '#10b981', gradient: 'from-emerald-500 to-teal-500' },
  ];
  
  const totalTenants = planDistribution.reduce((sum, item) => sum + item.value, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Tenants</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.tenants?.total || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">{stats?.tenants?.active || 0} active</Badge>
                  <Badge className="bg-blue-500/20 text-blue-400 text-xs">{stats?.tenants?.trial || 0} trial</Badge>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Users</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.users?.total || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">{stats?.users?.active || 0} active</Badge>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Cases</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.content?.cases || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-slate-400">{stats?.content?.clients || 0} clients</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Documents</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.content?.documents || 0}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-slate-400">{stats?.content?.invoices || 0} invoices</span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <FileText className="h-6 w-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenant Growth Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-400" />
              Tenant Growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tenantTrend}>
                  <defs>
                    <linearGradient id="tenantGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Area type="monotone" dataKey="tenants" stroke="#3b82f6" fill="url(#tenantGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Plan Distribution - Enhanced Design */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <PieChart className="h-5 w-5 text-purple-400" />
                Plan Distribution
              </CardTitle>
              <Badge className="bg-slate-700 text-slate-300 text-xs">
                {totalTenants} Total Firms
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {/* Pie Chart */}
              <div className="relative h-48 w-48 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={planDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {planDistribution.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #475569', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                      }}
                      formatter={(value, name) => [`${value} firms`, name]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-white">{totalTenants}</span>
                  <span className="text-xs text-slate-400">Firms</span>
                </div>
              </div>
              
              {/* Legend Cards */}
              <div className="flex-1 grid grid-cols-2 gap-3">
                {planDistribution.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-slate-500 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="h-3 w-3 rounded-full ring-2 ring-offset-1 ring-offset-slate-800" 
                        style={{ backgroundColor: item.color, ringColor: item.color + '40' }} 
                      />
                      <span className="text-sm font-medium text-slate-300">{item.name}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">{item.value}</span>
                      <span className="text-xs text-slate-500">
                        {totalTenants > 0 ? Math.round((item.value / totalTenants) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Trends */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <LineChart className="h-5 w-5 text-emerald-400" />
            Platform Usage Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usageTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  labelStyle={{ color: '#f8fafc' }}
                />
                <Bar dataKey="cases" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Cases" />
                <Bar dataKey="docs" fill="#10b981" radius={[4, 4, 0, 0]} name="Documents" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Console Component
export const AdminConsole = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [platformUser, setPlatformUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'dashboard');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [oauthCredentials, setOAuthCredentials] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemJobs, setSystemJobs] = useState([]);
  const [emailConnections, setEmailConnections] = useState([]);
  const [deletingConnection, setDeletingConnection] = useState(null);
  
  // Filter states
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantStatusFilter, setTenantStatusFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  
  // Dialog states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Invite Tenant Dialog
  const [inviteTenantDialog, setInviteTenantDialog] = useState(false);
  const [inviteTenantForm, setInviteTenantForm] = useState({
    firm_name: '', owner_email: '', owner_name: '', plan: 'trial'
  });
  const [invitingTenant, setInvitingTenant] = useState(false);
  
  // Invite Platform User Dialog
  const [inviteStaffDialog, setInviteStaffDialog] = useState(false);
  const [inviteStaffForm, setInviteStaffForm] = useState({
    email: '', name: '', password: '', role: 'support_agent'
  });
  const [invitingStaff, setInvitingStaff] = useState(false);
  
  // Tenant Detail Dialog
  const [tenantDetailDialog, setTenantDetailDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // User Detail/Edit Dialog
  const [userDetailDialog, setUserDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    name: '', email: '', role: '', is_active: true
  });
  const [savingUser, setSavingUser] = useState(false);
  
  // Impersonation Dialog
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [impersonateForm, setImpersonateForm] = useState({
    tenant_id: '', reason: '', ticket_id: '', duration: 15
  });
  const [impersonating, setImpersonating] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState(null);
  
  // Subscription Dialog
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    tenant_id: '', action: '', plan: '', days: 14, amount: 0, reason: ''
  });
  
  // OAuth Dialog
  const [oauthDialog, setOAuthDialog] = useState(false);
  const [oauthForm, setOAuthForm] = useState({
    provider: '', client_id: '', client_secret: '', redirect_uri: ''
  });
  const [savingOAuth, setSavingOAuth] = useState(false);
  
  // Write Mode Upgrade Dialog
  const [writeModeDialog, setWriteModeDialog] = useState(false);
  const [writeModeForm, setWriteModeForm] = useState({
    session_id: '', confirmation_password: '', reason: '', ticket_id: ''
  });
  const [upgradingWriteMode, setUpgradingWriteMode] = useState(false);
  
  // Update URL when section changes
  useEffect(() => {
    setSearchParams({ section: activeSection });
  }, [activeSection, setSearchParams]);
  
  // Check if admin token exists
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      checkAdminAuth();
    } else {
      setLoading(false);
    }
    
    // Check for active impersonation
    const impSession = localStorage.getItem('impersonation_session');
    if (impSession) {
      setImpersonationSession(JSON.parse(impSession));
    }
  }, []);
  
  const checkAdminAuth = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      
      const response = await adminAPI.me();
      setPlatformUser(response.data);
      setIsAuthenticated(true);
      loadData();
    } catch (error) {
      console.error('Admin auth check failed:', error);
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await adminAPI.login(loginForm);
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('token', response.data.token);
      setPlatformUser(response.data.user);
      setIsAuthenticated(true);
      loadData();
      toast.success('Logged in to Admin Console');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    if (impersonationSession) {
      localStorage.removeItem('impersonation_session');
      setImpersonationSession(null);
    }
    setIsAuthenticated(false);
    setPlatformUser(null);
  };
  
  const loadData = useCallback(async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      
      const [statsRes, tenantsRes, usersRes, logsRes, flagsRes, subsRes, platformUsersRes, oauthRes, healthRes, jobsRes, emailConnsRes] = await Promise.all([
        adminAPI.getSystemStats().catch(() => ({ data: null })),
        adminAPI.listTenants({}).catch(() => ({ data: { tenants: [] } })),
        adminAPI.listUsers({}).catch(() => ({ data: { users: [] } })),
        adminAPI.listAuditLogs({ limit: 100 }).catch(() => ({ data: { logs: [] } })),
        adminAPI.listFeatureFlags({}).catch(() => ({ data: { flags: [], global_defaults: [] } })),
        adminAPI.listSubscriptions({}).catch(() => ({ data: { subscriptions: [] } })),
        adminAPI.listPlatformUsers().catch(() => ({ data: [] })),
        adminAPI.getOAuthCredentials().catch(() => ({ data: { credentials: [] } })),
        adminAPI.getSystemHealth().catch(() => ({ data: null })),
        adminAPI.listSystemJobs({ limit: 50 }).catch(() => ({ data: { jobs: [], summary: {} } })),
        adminAPI.listEmailConnections().catch(() => ({ data: { connections: [] } })),
      ]);
      
      setStats(statsRes.data);
      setTenants(tenantsRes.data?.tenants || []);
      setUsers(usersRes.data?.users || []);
      setAuditLogs(logsRes.data?.logs || []);
      setFeatureFlags(flagsRes.data?.flags || []);
      setSubscriptions(subsRes.data?.subscriptions || []);
      setPlatformUsers(platformUsersRes.data || []);
      setOAuthCredentials(oauthRes.data?.credentials || []);
      setSystemHealth(healthRes.data);
      setSystemJobs(jobsRes.data?.jobs || []);
      setEmailConnections(emailConnsRes.data?.connections || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, []);
  
  // Auto-refresh data every 10 minutes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const AUTO_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes in ms
    
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing admin data...');
      loadData();
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, loadData]);
  
  // Invite Tenant Handler
  const handleInviteTenant = async (e) => {
    e.preventDefault();
    setInvitingTenant(true);
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      await adminAPI.inviteTenant(inviteTenantForm);
      toast.success(`Invitation sent to ${inviteTenantForm.owner_email}`);
      setInviteTenantDialog(false);
      setInviteTenantForm({ firm_name: '', owner_email: '', owner_name: '', plan: 'trial' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInvitingTenant(false);
    }
  };
  
  // Invite Staff Handler
  const handleInviteStaff = async (e) => {
    e.preventDefault();
    setInvitingStaff(true);
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      await adminAPI.createPlatformUser(inviteStaffForm);
      toast.success(`Platform user ${inviteStaffForm.name} created`);
      setInviteStaffDialog(false);
      setInviteStaffForm({ email: '', name: '', password: '', role: 'support_agent' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setInvitingStaff(false);
    }
  };
  
  // View Tenant Detail
  const handleViewTenant = async (tenant) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      const response = await adminAPI.getTenant(tenant.firm_id);
      setSelectedTenant(response.data);
      setTenantDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load tenant details');
    }
  };
  
  // Start Impersonation
  const handleStartImpersonation = async (e) => {
    e.preventDefault();
    setImpersonating(true);
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      const response = await adminAPI.startImpersonation(impersonateForm);
      const session = {
        ...response.data,
        started_at: new Date().toISOString(),
        expires_at: response.data.expires_at || new Date(Date.now() + impersonateForm.duration * 60 * 1000).toISOString()
      };
      
      // Store the impersonation session and token
      localStorage.setItem('impersonation_session', JSON.stringify(session));
      localStorage.setItem('impersonation_token', response.data.token);
      
      // Set the impersonation token as the active token for API calls
      localStorage.setItem('token', response.data.token);
      
      setImpersonateDialog(false);
      setImpersonateForm({ tenant_id: '', reason: '', ticket_id: '', duration: 15 });
      toast.success(`Entering ${session.tenant_name}'s dashboard...`);
      
      // Navigate directly to the tenant's dashboard (same window for seamless experience)
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start impersonation');
    } finally {
      setImpersonating(false);
    }
  };
  
  // Quick Impersonate (without dialog, for direct access)
  const handleQuickImpersonate = async (tenant) => {
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    const reason = 'Quick access for support troubleshooting';
    const ticket_id = `QUICK-${Date.now()}`;
    
    try {
      const response = await adminAPI.startImpersonation({
        tenant_id: tenant.firm_id,
        reason,
        ticket_id,
        duration: 30
      });
      
      const session = {
        ...response.data,
        started_at: new Date().toISOString(),
        expires_at: response.data.expires_at || new Date(Date.now() + 30 * 60 * 1000).toISOString()
      };
      
      localStorage.setItem('impersonation_session', JSON.stringify(session));
      localStorage.setItem('impersonation_token', response.data.token);
      localStorage.setItem('token', response.data.token);
      
      toast.success(`Entering ${tenant.name}'s dashboard...`);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start impersonation');
    }
  };
  
  // End Impersonation
  const handleEndImpersonation = async () => {
    try {
      await adminAPI.endImpersonation();
      localStorage.removeItem('impersonation_session');
      setImpersonationSession(null);
      toast.success('Impersonation ended');
    } catch (error) {
      toast.error('Failed to end impersonation');
    }
  };
  
  // Update Tenant Status
  const handleUpdateTenantStatus = async (tenantId, status, reason) => {
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      await adminAPI.updateTenantStatus(tenantId, { status, reason });
      toast.success(`Tenant status updated to ${status}`);
      loadData();
      setTenantDetailDialog(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // View User Details
  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setUserEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      is_active: user.is_active !== false
    });
    setUserDetailDialog(true);
  };
  
  // Save User Changes
  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      await adminAPI.updateUser(selectedUser.user_id, userEditForm);
      toast.success('User updated successfully');
      loadData();
      setUserDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };
  
  // Toggle User Status (Enable/Disable)
  const handleToggleUserStatus = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      if (selectedUser.is_active !== false) {
        await adminAPI.disableUser(selectedUser.user_id, { reason: 'Disabled by admin' });
        toast.success('User disabled');
      } else {
        await adminAPI.enableUser(selectedUser.user_id);
        toast.success('User enabled');
      }
      loadData();
      setUserDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setSavingUser(false);
    }
  };
  
  // Reset User Password
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    const adminToken = localStorage.getItem('admin_token');
    localStorage.setItem('token', adminToken);
    
    try {
      await adminAPI.resetPassword(selectedUser.user_id);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };
  
  // Filtered data
  const filteredTenants = tenants.filter(t => {
    const matchesSearch = !tenantSearch || 
      t.name?.toLowerCase().includes(tenantSearch.toLowerCase()) ||
      t.firm_id?.toLowerCase().includes(tenantSearch.toLowerCase());
    const matchesStatus = tenantStatusFilter === 'all' || t.subscription_status === tenantStatusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const filteredUsers = users.filter(u => {
    return !userSearch || 
      u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase());
  });

  // Login Screen
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzFmMjkzNyIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" />
        
        <Card className="w-full max-w-md bg-slate-800/80 border-slate-700 backdrop-blur-xl shadow-2xl relative z-10">
          <CardHeader className="text-center pb-2">
            <div className="h-16 w-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30">
              <Shield className="h-9 w-9 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Console</CardTitle>
            <CardDescription className="text-slate-400">LEAP Pro Platform Administration</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="admin@leappro.ca"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="admin-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  required
                  data-testid="admin-password"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11" disabled={loginLoading}>
                {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="admin-console">
      {/* Impersonation Banner */}
      {impersonationSession && (
        <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between ${
          impersonationSession.access_level === 'write' ? 'bg-red-500 text-white' : 'bg-amber-500 text-amber-900'
        }`}>
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5" />
            <span className="font-medium">
              ADMIN VIEW — Impersonating Tenant: {impersonationSession.tenant_name}
            </span>
            <Badge className={impersonationSession.access_level === 'write' ? 'bg-red-700 text-white' : 'bg-amber-600 text-white'}>
              {impersonationSession.access_level === 'write' ? '⚠️ WRITE MODE' : 'Read-Only'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {platformUser?.role === 'platform_owner' && impersonationSession.access_level !== 'write' && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => {
                  setWriteModeForm({ ...writeModeForm, session_id: impersonationSession.session_id });
                  setWriteModeDialog(true);
                }}
                className="border-amber-700 text-amber-900 hover:bg-amber-600"
              >
                <Unlock className="h-4 w-4 mr-1" />
                Enable Write Mode
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleEndImpersonation} 
              className={impersonationSession.access_level === 'write' ? 'border-red-300 text-white hover:bg-red-600' : 'border-amber-700 text-amber-900 hover:bg-amber-600'}
            >
              <X className="h-4 w-4 mr-1" />
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}
      
      {/* Sidebar */}
      <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} platformUser={platformUser} />
      
      {/* Main Content */}
      <main className={`ml-64 ${impersonationSession ? 'pt-12' : ''}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white capitalize">{activeSection.replace('-', ' ')}</h1>
              <p className="text-sm text-slate-400">Manage your platform</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadData} className="gap-2 border-slate-700 text-slate-300 hover:bg-slate-800">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        
        <div className="p-6">
          {/* Dashboard */}
          {activeSection === 'dashboard' && (
            <DashboardSection stats={stats} metrics={metrics} loading={loading} />
          )}
          
          {/* Tenants Section */}
          {activeSection === 'tenants' && (
            <div className="space-y-6">
              {/* Header with actions */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search tenants..."
                      value={tenantSearch}
                      onChange={(e) => setTenantSearch(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <Select value={tenantStatusFilter} onValueChange={setTenantStatusFilter}>
                    <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setInviteTenantDialog(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4" />
                  Invite New Firm
                </Button>
              </div>
              
              {/* Tenants Table */}
              <Card className="bg-slate-800/50 border-slate-700">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">Tenant</TableHead>
                      <TableHead className="text-slate-400">Plan</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Users</TableHead>
                      <TableHead className="text-slate-400">Cases</TableHead>
                      <TableHead className="text-slate-400">Last Activity</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTenants.map((tenant) => (
                      <TableRow key={tenant.firm_id} className="border-slate-700">
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{tenant.name}</p>
                            <p className="text-xs text-slate-500 font-mono">{tenant.firm_id}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(tenant.plan || 'trial')}>
                            {tenant.plan || 'trial'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(tenant.subscription_status)}>
                            {tenant.subscription_status || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{tenant.user_count}</TableCell>
                        <TableCell className="text-slate-300">{tenant.case_count}</TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {tenant.last_activity ? formatRelativeTime(tenant.last_activity) : 'Never'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleViewTenant(tenant)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleQuickImpersonate(tenant)}
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                              title="Quick Access - View Tenant Dashboard"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                setImpersonateForm({ ...impersonateForm, tenant_id: tenant.firm_id });
                                setImpersonateDialog(true);
                              }}
                              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                              title="Impersonate with Options"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}
          
          {/* Users Section */}
          {activeSection === 'users' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">User</TableHead>
                      <TableHead className="text-slate-400">Firm</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.user_id} className="border-slate-700">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-slate-600 text-white text-xs">
                                {user.name?.charAt(0) || user.email?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-white">{user.name}</p>
                              <p className="text-xs text-slate-400">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300">{user.firm_name || '-'}</TableCell>
                        <TableCell>
                          <Select
                            defaultValue={user.role}
                            onValueChange={async (newRole) => {
                              const adminToken = localStorage.getItem('admin_token');
                              localStorage.setItem('token', adminToken);
                              try {
                                await api.put(`/admin/users/${user.user_id}/role`, { role: newRole });
                                toast.success(`Role updated to ${newRole}`);
                                loadData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to update role');
                              }
                            }}
                          >
                            <SelectTrigger className="w-32 h-8 bg-slate-700 border-slate-600 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600">
                              <SelectItem value="owner" className="text-white hover:bg-slate-600">Owner</SelectItem>
                              <SelectItem value="admin" className="text-white hover:bg-slate-600">Admin</SelectItem>
                              <SelectItem value="partner" className="text-white hover:bg-slate-600">Partner</SelectItem>
                              <SelectItem value="associate" className="text-white hover:bg-slate-600">Associate</SelectItem>
                              <SelectItem value="paralegal" className="text-white hover:bg-slate-600">Paralegal</SelectItem>
                              <SelectItem value="staff" className="text-white hover:bg-slate-600">Staff</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {user.is_active !== false ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-400 hover:text-blue-300"
                              onClick={() => handleViewUser(user)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}
          
          {/* Platform Team Section */}
          {activeSection === 'platform-team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">Platform Team Members</h2>
                  <p className="text-sm text-slate-400">Manage administrators and support staff</p>
                </div>
                <Button onClick={() => setInviteStaffDialog(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="h-4 w-4" />
                  Add Team Member
                </Button>
              </div>
              
              {/* Role Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(PLATFORM_ROLES).map(([roleKey, role]) => {
                  const roleUsers = platformUsers.filter(u => u.role === roleKey);
                  return (
                    <Card key={roleKey} className="bg-slate-800/50 border-slate-700">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge className={role.color}>{role.label}</Badge>
                          <span className="text-2xl font-bold text-white">{roleUsers.length}</span>
                        </div>
                        <CardDescription className="text-slate-400 text-xs mt-1">
                          {role.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {roleUsers.slice(0, 3).map(user => (
                            <div key={user.user_id} className="flex items-center gap-2 text-sm">
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="bg-slate-600 text-white text-xs">
                                  {user.name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-slate-300 truncate">{user.name}</span>
                            </div>
                          ))}
                          {roleUsers.length > 3 && (
                            <p className="text-xs text-slate-500">+{roleUsers.length - 3} more</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Full Team List */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">All Team Members</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Email</TableHead>
                        <TableHead className="text-slate-400">Role</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Last Login</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {platformUsers.map((user) => (
                        <TableRow key={user.user_id} className="border-slate-700">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-blue-600 text-white text-sm">
                                  {user.name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-white">{user.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300">{user.email}</TableCell>
                          <TableCell>
                            <Badge className={PLATFORM_ROLES[user.role]?.color || 'bg-slate-500/20 text-slate-400'}>
                              {PLATFORM_ROLES[user.role]?.label || user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {user.is_active !== false ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400">Disabled</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {user.last_login ? formatRelativeTime(user.last_login) : 'Never'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Subscriptions Section */}
          {activeSection === 'subscriptions' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Zap className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Trial</p>
                        <p className="text-2xl font-bold text-white">{tenants.filter(t => t.plan === 'trial' || !t.plan).length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Professional</p>
                        <p className="text-2xl font-bold text-white">{tenants.filter(t => t.plan === 'professional').length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Enterprise</p>
                        <p className="text-2xl font-bold text-white">{tenants.filter(t => t.plan === 'enterprise').length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Manage Subscriptions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">Tenant</TableHead>
                        <TableHead className="text-slate-400">Plan</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Trial Ends</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.firm_id} className="border-slate-700">
                          <TableCell className="font-medium text-white">{tenant.name}</TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(tenant.plan || 'trial')}>
                              {tenant.plan || 'trial'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(tenant.subscription_status)}>
                              {tenant.subscription_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {tenant.trial_ends_at ? formatDate(tenant.trial_ends_at) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-purple-400 hover:text-purple-300"
                                onClick={() => {
                                  setSubscriptionForm({ ...subscriptionForm, tenant_id: tenant.firm_id, action: 'change_plan' });
                                  setSubscriptionDialog(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-emerald-400 hover:text-emerald-300"
                                onClick={() => {
                                  setSubscriptionForm({ ...subscriptionForm, tenant_id: tenant.firm_id, action: 'extend_trial' });
                                  setSubscriptionDialog(true);
                                }}
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-blue-400 hover:text-blue-300"
                                onClick={() => {
                                  setSubscriptionForm({ ...subscriptionForm, tenant_id: tenant.firm_id, action: 'apply_credit' });
                                  setSubscriptionDialog(true);
                                }}
                              >
                                <DollarSign className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Billing & Pricing Config Section (Combined Pricing + Promo Codes) */}
          {activeSection === 'billing-config' && (
            <BillingConfigSection />
          )}
          
          {/* Roles & Permissions Section */}
          {activeSection === 'roles' && (
            <RolesSection />
          )}
          
          {/* Feature Flags Section */}
          {activeSection === 'feature-flags' && (
            <div className="space-y-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Flag className="h-5 w-5 text-purple-400" />
                    Global Feature Flags
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Control platform-wide feature availability
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {featureFlags.filter(f => f.tenant_id === '__global__').map((flag) => (
                      <div key={flag.flag_name} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                        <div>
                          <p className="font-medium text-white">{flag.flag_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                          <p className="text-xs text-slate-400">Updated: {formatRelativeTime(flag.updated_at)}</p>
                        </div>
                        <Switch
                          checked={flag.enabled}
                          onCheckedChange={async (checked) => {
                            const adminToken = localStorage.getItem('admin_token');
                            localStorage.setItem('token', adminToken);
                            try {
                              await adminAPI.updateFeatureFlag('__global__', { flag_name: flag.flag_name, enabled: checked });
                              toast.success(`${flag.flag_name} ${checked ? 'enabled' : 'disabled'}`);
                              loadData();
                            } catch (error) {
                              toast.error('Failed to update flag');
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Audit Logs Section */}
          {activeSection === 'audit-logs' && (
            <div className="space-y-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-400" />
                    Audit Logs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-2">
                      {auditLogs.map((log, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-slate-300 border-slate-500">
                                  {log.action_type}
                                </Badge>
                                <span className="text-xs text-slate-400">by {log.actor_role}</span>
                                {log.tenant_id && (
                                  <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                    {log.tenant_id}
                                  </Badge>
                                )}
                              </div>
                              {log.reason && (
                                <p className="text-sm text-slate-300 mt-2">Reason: {log.reason}</p>
                              )}
                              {log.action_details && Object.keys(log.action_details).length > 0 && (
                                <p className="text-xs text-slate-400 mt-1 font-mono bg-slate-800 p-2 rounded">
                                  {JSON.stringify(log.action_details)}
                                </p>
                              )}
                            </div>
                            <div className="text-right shrink-0 ml-4">
                              <p className="text-sm text-slate-400">{formatDateTime(log.timestamp)}</p>
                              {log.ip_address && (
                                <p className="text-xs text-slate-500">{log.ip_address}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* OAuth Settings Section */}
          {activeSection === 'oauth-settings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Key className="h-5 w-5 text-amber-400" />
                    OAuth Credentials
                  </h2>
                  <p className="text-sm text-slate-400">Manage platform-wide OAuth provider credentials</p>
                </div>
              </div>
              
              {platformUser?.role !== 'platform_owner' && (
                <Card className="bg-amber-500/10 border-amber-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                      <p className="text-sm text-amber-300">
                        Only Platform Owners can view and edit OAuth credentials. Contact your administrator.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Google OAuth */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Globe className="h-5 w-5 text-red-400" />
                      Google OAuth
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Gmail & Google Calendar integration
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(() => {
                      const googleCred = oauthCredentials.find(c => c.provider === 'google');
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-400">Status</span>
                            {googleCred?.configured ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400">Configured</Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400">Not Configured</Badge>
                            )}
                          </div>
                          {googleCred?.client_id && (
                            <div>
                              <span className="text-xs text-slate-500">Client ID</span>
                              <p className="text-sm text-slate-300 font-mono bg-slate-700/50 p-2 rounded mt-1 break-all">
                                {googleCred.client_id}
                              </p>
                            </div>
                          )}
                          {googleCred?.updated_at && (
                            <p className="text-xs text-slate-500">
                              Last updated: {formatRelativeTime(googleCred.updated_at)}
                            </p>
                          )}
                          <div className="space-y-2">
                            <span className="text-xs text-slate-500">Redirect URI</span>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-slate-700 p-2 rounded text-emerald-400 break-all">
                                https://case-ledger.preview.emergentagent.com/api/email/callback/gmail
                              </code>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText('https://case-ledger.preview.emergentagent.com/api/email/callback/gmail');
                                  toast.success('Copied to clipboard');
                                }}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {platformUser?.role === 'platform_owner' && (
                            <Button 
                              className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                setOAuthForm({
                                  provider: 'google',
                                  client_id: googleCred?.client_id || '',
                                  client_secret: '',
                                  redirect_uri: googleCred?.redirect_uri || ''
                                });
                                setOAuthDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {googleCred?.configured ? 'Update Credentials' : 'Configure'}
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
                
                {/* Microsoft OAuth */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Globe className="h-5 w-5 text-blue-400" />
                      Microsoft OAuth
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Outlook & Microsoft 365 integration
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(() => {
                      const msCred = oauthCredentials.find(c => c.provider === 'microsoft');
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-400">Status</span>
                            {msCred?.configured ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400">Configured</Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400">Not Configured</Badge>
                            )}
                          </div>
                          {msCred?.client_id && (
                            <div>
                              <span className="text-xs text-slate-500">Application ID</span>
                              <p className="text-sm text-slate-300 font-mono bg-slate-700/50 p-2 rounded mt-1 break-all">
                                {msCred.client_id}
                              </p>
                            </div>
                          )}
                          {msCred?.updated_at && (
                            <p className="text-xs text-slate-500">
                              Last updated: {formatRelativeTime(msCred.updated_at)}
                            </p>
                          )}
                          {platformUser?.role === 'platform_owner' && (
                            <Button 
                              className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                setOAuthForm({
                                  provider: 'microsoft',
                                  client_id: msCred?.client_id || '',
                                  client_secret: '',
                                  redirect_uri: msCred?.redirect_uri || ''
                                });
                                setOAuthDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {msCred?.configured ? 'Update Credentials' : 'Configure'}
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>

              {/* Stripe Payment Configuration */}
              <div className="mt-8">
                <h3 className="text-md font-semibold text-white flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-purple-400" />
                  Payment Processing (Stripe)
                </h3>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" fill="#6772E5"/>
                      </svg>
                      Platform Stripe Configuration
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Configure Stripe to collect subscription payments from your customers
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">Stripe Connected</p>
                          <p className="text-xs text-slate-400">Test mode active (sk_test_***)</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-500/20 text-amber-300">Test Mode</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-slate-700/30">
                        <p className="text-xs text-slate-500 mb-1">Webhook Status</p>
                        <p className="text-sm text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Active
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-700/30">
                        <p className="text-xs text-slate-500 mb-1">Connected Accounts</p>
                        <p className="text-sm text-white">0 firms connected</p>
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-700 pt-4">
                      <p className="text-sm text-slate-400 mb-3">To use your own Stripe account:</p>
                      <ol className="text-sm text-slate-300 space-y-2">
                        <li className="flex gap-2">
                          <span className="flex-shrink-0 h-5 w-5 rounded-full bg-slate-600 flex items-center justify-center text-xs">1</span>
                          <span>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Stripe Dashboard → API Keys</a></span>
                        </li>
                        <li className="flex gap-2">
                          <span className="flex-shrink-0 h-5 w-5 rounded-full bg-slate-600 flex items-center justify-center text-xs">2</span>
                          <span>Copy your Secret Key (starts with <code className="bg-slate-700 px-1 rounded">sk_live_</code> or <code className="bg-slate-700 px-1 rounded">sk_test_</code>)</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="flex-shrink-0 h-5 w-5 rounded-full bg-slate-600 flex items-center justify-center text-xs">3</span>
                          <span>Contact support to update your platform Stripe keys</span>
                        </li>
                      </ol>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="gap-2" onClick={() => window.open('https://dashboard.stripe.com', '_blank')}>
                        <ExternalLink className="h-4 w-4" />
                        Open Stripe Dashboard
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Security Notice */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Lock className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-white">Security Information</p>
                      <ul className="text-sm text-slate-400 mt-2 space-y-1">
                        <li>• Credentials are encrypted at rest</li>
                        <li>• Only Platform Owners can view/edit credentials</li>
                        <li>• All changes are logged in the audit trail</li>
                        <li>• Credential rotation history is maintained</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* System Health Section */}
          {activeSection === 'system-health' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Activity className="h-5 w-5 text-emerald-400" />
                    System Health
                  </h2>
                  <p className="text-sm text-slate-400">Monitor system performance and errors</p>
                </div>
                <Button variant="outline" size="sm" onClick={loadData} className="gap-2 border-slate-700">
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
              
              {/* Health Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Overall Status</p>
                        <p className="text-xl font-bold text-white mt-1 capitalize">{systemHealth?.overall_status || 'Unknown'}</p>
                      </div>
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        systemHealth?.overall_status === 'healthy' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                      }`}>
                        {systemHealth?.overall_status === 'healthy' ? (
                          <CheckCircle className="h-5 w-5 text-emerald-400" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-400" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Failed Jobs (24h)</p>
                        <p className="text-xl font-bold text-white mt-1">{systemHealth?.failed_jobs?.last_24h || 0}</p>
                      </div>
                      <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Webhook Failures</p>
                        <p className="text-xl font-bold text-white mt-1">{systemHealth?.webhook_failures_24h || 0}</p>
                      </div>
                      <div className="h-10 w-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Zap className="h-5 w-5 text-amber-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">API Requests (24h)</p>
                        <p className="text-xl font-bold text-white mt-1">{systemHealth?.api_health?.requests_24h || 0}</p>
                      </div>
                      <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Error Trend Chart */}
              {systemHealth?.error_trend && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-red-400" />
                      Error Trend (Last 7 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={systemHealth.error_trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                            labelStyle={{ color: '#f8fafc' }}
                          />
                          <Bar dataKey="errors" fill="#ef4444" radius={[4, 4, 0, 0]} name="Errors" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Error by Type */}
              {systemHealth?.error_by_type && systemHealth.error_by_type.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Errors by Type (Last 7 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {systemHealth.error_by_type.map((error, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                          <div className="flex items-center gap-3">
                            <AlertCircle className="h-4 w-4 text-red-400" />
                            <span className="text-slate-300 capitalize">{error.type?.replace(/_/g, ' ')}</span>
                          </div>
                          <Badge className="bg-red-500/20 text-red-400">{error.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Database & API Health */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Database className="h-5 w-5 text-purple-400" />
                      Database Health
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      {systemHealth?.db_health?.status === 'healthy' ? (
                        <CheckCircle className="h-6 w-6 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="h-6 w-6 text-amber-400" />
                      )}
                      <div>
                        <p className="text-white font-medium capitalize">{systemHealth?.db_health?.status || 'Unknown'}</p>
                        <p className="text-sm text-slate-400">{systemHealth?.db_health?.connection || 'Checking...'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-400" />
                      API Health
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      {systemHealth?.api_health?.status === 'healthy' ? (
                        <CheckCircle className="h-6 w-6 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="h-6 w-6 text-amber-400" />
                      )}
                      <div>
                        <p className="text-white font-medium capitalize">{systemHealth?.api_health?.status || 'Unknown'}</p>
                        <p className="text-sm text-slate-400">
                          Avg response: {systemHealth?.api_health?.avg_response_time_ms || 0}ms
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent System Jobs */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Recent Background Jobs</CardTitle>
                </CardHeader>
                <CardContent>
                  {systemJobs.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No background jobs recorded</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400">Job Type</TableHead>
                          <TableHead className="text-slate-400">Status</TableHead>
                          <TableHead className="text-slate-400">Tenant</TableHead>
                          <TableHead className="text-slate-400">Created</TableHead>
                          <TableHead className="text-slate-400">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {systemJobs.slice(0, 10).map((job) => (
                          <TableRow key={job.job_id} className="border-slate-700">
                            <TableCell className="text-slate-300 capitalize">{job.job_type?.replace(/_/g, ' ')}</TableCell>
                            <TableCell>
                              <Badge className={
                                job.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                job.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-500/20 text-slate-400'
                              }>
                                {job.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">{job.tenant_id || '-'}</TableCell>
                            <TableCell className="text-slate-400 text-sm">{formatRelativeTime(job.created_at)}</TableCell>
                            <TableCell>
                              {job.status === 'failed' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-blue-400 hover:text-blue-300"
                                  onClick={async () => {
                                    const adminToken = localStorage.getItem('admin_token');
                                    localStorage.setItem('token', adminToken);
                                    try {
                                      await adminAPI.retryJob(job.job_id);
                                      toast.success('Job queued for retry');
                                      loadData();
                                    } catch (error) {
                                      toast.error('Failed to retry job');
                                    }
                                  }}
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div className="space-y-6">
              {/* Platform Configuration */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Settings className="h-5 w-5 text-blue-400" />
                    Platform Configuration
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Core platform settings and branding
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Platform Name</Label>
                      <Input
                        defaultValue="LEAP Pro"
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Your Platform Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Support Email</Label>
                      <Input
                        defaultValue="support@leappro.ca"
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="support@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Default Trial Days</Label>
                      <Input
                        type="number"
                        defaultValue="14"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Max Users per Firm</Label>
                      <Input
                        type="number"
                        defaultValue="50"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Your Permissions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-400" />
                    Your Admin Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {platformUser?.permissions?.map(p => (
                      <Badge key={p} variant="outline" className="text-xs text-emerald-300 border-emerald-500/50 bg-emerald-500/10">{p}</Badge>
                    ))}
                    {(!platformUser?.permissions || platformUser.permissions.length === 0) && (
                      <p className="text-slate-400 text-sm">Full platform access</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Feature Flags */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <ToggleRight className="h-5 w-5 text-purple-400" />
                    Feature Flags
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Enable or disable platform features globally
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">AI Assistant</p>
                      <p className="text-xs text-slate-400">Enable AI-powered document analysis</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">Team Chat</p>
                      <p className="text-xs text-slate-400">Enable real-time team messaging</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">Client Portal</p>
                      <p className="text-xs text-slate-400">Allow clients to access portal</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">Email Integration</p>
                      <p className="text-xs text-slate-400">Gmail/Outlook email sync</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">Calendar Sync</p>
                      <p className="text-xs text-slate-400">External calendar integration</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="font-medium text-white">Trust Accounting</p>
                      <p className="text-xs text-slate-400">Enable trust account management</p>
                    </div>
                    <Switch defaultChecked className="data-[state=checked]:bg-emerald-500" />
                  </div>
                </CardContent>
              </Card>

              {/* OAuth Redirect URIs */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-amber-400" />
                    OAuth Redirect URIs
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Add these URIs to your OAuth provider settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-300">Gmail OAuth Callback</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-slate-900 p-3 rounded text-emerald-400 font-mono">
                        https://case-ledger.preview.emergentagent.com/api/email/callback/gmail
                      </code>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          navigator.clipboard.writeText('https://case-ledger.preview.emergentagent.com/api/email/callback/gmail');
                          toast.success('Copied to clipboard');
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-300">Outlook OAuth Callback</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-slate-900 p-3 rounded text-blue-400 font-mono">
                        https://case-ledger.preview.emergentagent.com/api/email/callback/outlook
                      </code>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          navigator.clipboard.writeText('https://case-ledger.preview.emergentagent.com/api/email/callback/outlook');
                          toast.success('Copied to clipboard');
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-300">Google Auth Callback</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-slate-900 p-3 rounded text-purple-400 font-mono">
                        https://case-ledger.preview.emergentagent.com/api/auth/google/callback
                      </code>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          navigator.clipboard.writeText('https://case-ledger.preview.emergentagent.com/api/auth/google/callback');
                          toast.success('Copied to clipboard');
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Connections Management */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5 text-red-400" />
                    Email Connections Management
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    View and manage email connections across all tenants ({emailConnections.length} total)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {emailConnections.length === 0 ? (
                    <div className="text-center py-6">
                      <Mail className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                      <p className="text-slate-400">No email connections found</p>
                      <p className="text-xs text-slate-500">Users have not connected their email accounts yet</p>
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[400px]">
                      <div className="space-y-3">
                        {emailConnections.map((conn, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                conn.provider === 'gmail' ? 'bg-red-500/20' : 'bg-blue-500/20'
                              }`}>
                                {conn.provider === 'gmail' ? (
                                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path fill="#00a4ef" d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-white">{conn.email}</p>
                                <p className="text-xs text-slate-400">
                                  {conn.firm_name} • {conn.user_name || conn.user_email || 'Unknown User'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={conn.valid_token !== false ? 'bg-emerald-500/20 text-emerald-400 text-xs' : 'bg-amber-500/20 text-amber-400 text-xs'}>
                                    {conn.valid_token !== false ? 'Active' : 'Token Expired'}
                                  </Badge>
                                  <span className="text-xs text-slate-500 capitalize">{conn.provider}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                disabled={deletingConnection === `${conn.provider}_${conn.firm_id}_${conn.user_id}`}
                                onClick={async () => {
                                  setDeletingConnection(`${conn.provider}_${conn.firm_id}_${conn.user_id}`);
                                  try {
                                    await adminAPI.deleteEmailConnection(conn.provider, conn.firm_id, conn.user_id);
                                    toast.success(`${conn.provider} connection for ${conn.email} removed`);
                                    // Refresh connections
                                    const res = await adminAPI.listEmailConnections();
                                    setEmailConnections(res.data?.connections || []);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed to remove connection');
                                  } finally {
                                    setDeletingConnection(null);
                                  }
                                }}
                                data-testid={`delete-email-conn-${idx}`}
                              >
                                {deletingConnection === `${conn.provider}_${conn.firm_id}_${conn.user_id}` ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                  <p className="text-xs text-slate-500 mt-3">
                    Note: Removing a connection will require the user to reconnect their email account. This action is logged.
                  </p>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <Save className="h-4 w-4" />
                  Save All Settings
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Invite Tenant Dialog */}
      <Dialog open={inviteTenantDialog} onOpenChange={setInviteTenantDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-400" />
              Invite New Firm
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Send an invitation email to create a new firm account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteTenant} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Firm Name *</Label>
              <Input
                value={inviteTenantForm.firm_name}
                onChange={(e) => setInviteTenantForm({ ...inviteTenantForm, firm_name: e.target.value })}
                placeholder="Smith & Associates"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Owner Name *</Label>
                <Input
                  value={inviteTenantForm.owner_name}
                  onChange={(e) => setInviteTenantForm({ ...inviteTenantForm, owner_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Owner Email *</Label>
                <Input
                  type="email"
                  value={inviteTenantForm.owner_email}
                  onChange={(e) => setInviteTenantForm({ ...inviteTenantForm, owner_email: e.target.value })}
                  placeholder="john@smithlaw.com"
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Starting Plan</Label>
              <Select value={inviteTenantForm.plan} onValueChange={(v) => setInviteTenantForm({ ...inviteTenantForm, plan: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial (14 days)</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setInviteTenantDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 gap-2" disabled={invitingTenant}>
                {invitingTenant ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Invite Staff Dialog */}
      <Dialog open={inviteStaffDialog} onOpenChange={setInviteStaffDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-400" />
              Add Platform Team Member
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new administrator or support staff account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteStaff} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={inviteStaffForm.name}
                onChange={(e) => setInviteStaffForm({ ...inviteStaffForm, name: e.target.value })}
                placeholder="Jane Doe"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={inviteStaffForm.email}
                onChange={(e) => setInviteStaffForm({ ...inviteStaffForm, email: e.target.value })}
                placeholder="jane@leappro.ca"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Temporary Password *</Label>
              <Input
                type="password"
                value={inviteStaffForm.password}
                onChange={(e) => setInviteStaffForm({ ...inviteStaffForm, password: e.target.value })}
                placeholder="••••••••"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role *</Label>
              <Select value={inviteStaffForm.role} onValueChange={(v) => setInviteStaffForm({ ...inviteStaffForm, role: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PLATFORM_ROLES).map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <span>{role.label}</span>
                        <span className="text-xs text-slate-400">- {role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setInviteStaffDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 gap-2" disabled={invitingStaff}>
                {invitingStaff ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Tenant Detail Dialog */}
      <Dialog open={tenantDetailDialog} onOpenChange={setTenantDetailDialog}>
        <DialogContent className="sm:max-w-[800px] bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-400" />
              Tenant Details
            </DialogTitle>
          </DialogHeader>
          {selectedTenant && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Name</p>
                  <p className="font-medium text-white">{selectedTenant.firm?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Plan</p>
                  <Badge className={getStatusBadge(selectedTenant.firm?.plan || 'trial')}>
                    {selectedTenant.firm?.plan || 'trial'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Status</p>
                  <Badge className={getStatusBadge(selectedTenant.firm?.subscription_status)}>
                    {selectedTenant.firm?.subscription_status || 'unknown'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Created</p>
                  <p className="text-sm text-white">{formatDate(selectedTenant.firm?.created_at)}</p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 p-4 rounded-lg bg-slate-700/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-400">{selectedTenant.stats?.user_count || 0}</p>
                  <p className="text-xs text-slate-400">Users</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{selectedTenant.stats?.case_count || 0}</p>
                  <p className="text-xs text-slate-400">Cases</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-400">{selectedTenant.stats?.client_count || 0}</p>
                  <p className="text-xs text-slate-400">Clients</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{selectedTenant.stats?.document_count || 0}</p>
                  <p className="text-xs text-slate-400">Documents</p>
                </div>
              </div>
              
              {/* Integrations */}
              <div>
                <p className="text-sm font-medium text-white mb-2">Integrations</p>
                <div className="flex flex-wrap gap-2">
                  {selectedTenant.integration_settings?.google_configured && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Google</Badge>
                  )}
                  {selectedTenant.integration_settings?.microsoft_configured && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Microsoft</Badge>
                  )}
                  {selectedTenant.integration_settings?.docusign_configured && (
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">DocuSign</Badge>
                  )}
                  {selectedTenant.integration_settings?.stripe_configured && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Stripe</Badge>
                  )}
                  {!selectedTenant.integration_settings?.google_configured && 
                   !selectedTenant.integration_settings?.microsoft_configured && 
                   !selectedTenant.integration_settings?.docusign_configured && (
                    <span className="text-sm text-slate-500">No integrations configured</span>
                  )}
                </div>
              </div>
              
              {/* Users */}
              <div>
                <p className="text-sm font-medium text-white mb-2">Users ({selectedTenant.users?.length || 0})</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedTenant.users?.map(user => (
                    <div key={user.user_id} className="flex items-center justify-between p-2 rounded bg-slate-700/50">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="bg-slate-600 text-xs">{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm text-white">{user.name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize border-slate-500">{user.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-700">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => {
                    setImpersonateForm({ ...impersonateForm, tenant_id: selectedTenant.firm?.firm_id });
                    setTenantDetailDialog(false);
                    setImpersonateDialog(true);
                  }}
                >
                  <Eye className="h-4 w-4" />
                  Impersonate
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={() => handleUpdateTenantStatus(selectedTenant.firm?.firm_id, 'active', 'Activated by admin')}
                >
                  <CheckCircle className="h-4 w-4" />
                  Activate
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    const reason = prompt('Enter reason for suspension:');
                    if (reason) handleUpdateTenantStatus(selectedTenant.firm?.firm_id, 'suspended', reason);
                  }}
                >
                  <Ban className="h-4 w-4" />
                  Suspend
                </Button>
              </div>
              
              {/* Plan Assignment Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <p className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Subscription Management
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Select 
                    defaultValue={selectedTenant.firm?.subscription_plan || selectedTenant.firm?.plan || 'trial'}
                    onValueChange={async (value) => {
                      const reason = prompt('Reason for plan change:');
                      if (!reason) return;
                      try {
                        await adminAPI.assignTenantPlan(selectedTenant.firm?.firm_id, {
                          plan: value,
                          billing_cycle: 'monthly',
                          reason: reason
                        });
                        toast.success(`Plan changed to ${value}`);
                        loadData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to change plan');
                      }
                    }}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select Plan" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="free" className="text-white hover:bg-slate-600">Free (Admin Only)</SelectItem>
                      <SelectItem value="trial" className="text-white hover:bg-slate-600">Trial</SelectItem>
                      <SelectItem value="professional" className="text-white hover:bg-slate-600">Professional</SelectItem>
                      <SelectItem value="expert" className="text-white hover:bg-slate-600">Expert</SelectItem>
                      <SelectItem value="enterprise" className="text-white hover:bg-slate-600">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    onClick={async () => {
                      const days = prompt('Trial days to grant (default 14):') || '14';
                      const reason = prompt('Reason for granting trial:');
                      if (!reason) return;
                      try {
                        await adminAPI.grantTenantTrial(selectedTenant.firm?.firm_id, {
                          trial_days: parseInt(days),
                          trial_tier: 'professional',
                          reason: reason
                        });
                        toast.success(`${days}-day trial granted`);
                        loadData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to grant trial');
                      }
                    }}
                  >
                    <Clock className="h-4 w-4" />
                    Grant Trial
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* User Detail/Edit Dialog */}
      <Dialog open={userDetailDialog} onOpenChange={setUserDetailDialog}>
        <DialogContent className="sm:max-w-[600px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-400" />
              User Details
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View and edit user information
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6 mt-4">
              {/* User Info Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-lg">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={selectedUser.picture} />
                  <AvatarFallback className="bg-blue-600 text-white text-xl">
                    {selectedUser.name?.charAt(0) || selectedUser.email?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{selectedUser.name || 'No Name'}</h3>
                  <p className="text-slate-400">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={selectedUser.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                      {selectedUser.is_active !== false ? 'Active' : 'Disabled'}
                    </Badge>
                    <Badge variant="outline" className="text-slate-300 capitalize border-slate-600">
                      {selectedUser.role?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {/* Edit Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Name</Label>
                    <Input
                      value={userEditForm.name}
                      onChange={(e) => setUserEditForm({ ...userEditForm, name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="User's name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      value={userEditForm.email}
                      onChange={(e) => setUserEditForm({ ...userEditForm, email: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="user@example.com"
                      type="email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Role</Label>
                  <Select value={userEditForm.role} onValueChange={(value) => setUserEditForm({ ...userEditForm, role: value })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="senior_lawyer">Senior Lawyer</SelectItem>
                      <SelectItem value="associate">Associate</SelectItem>
                      <SelectItem value="paralegal">Paralegal</SelectItem>
                      <SelectItem value="legal_assistant">Legal Assistant</SelectItem>
                      <SelectItem value="accountant">Accountant</SelectItem>
                      <SelectItem value="receptionist">Receptionist</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* User Metadata */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-700/30 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">User ID</p>
                    <p className="text-sm text-slate-300 font-mono">{selectedUser.user_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Tenant/Firm</p>
                    <p className="text-sm text-slate-300">{selectedUser.firm_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-sm text-slate-300">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Last Login</p>
                    <p className="text-sm text-slate-300">{selectedUser.last_login ? formatDate(selectedUser.last_login) : 'Never'}</p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleUserStatus}
                    disabled={savingUser}
                    className={selectedUser.is_active !== false 
                      ? "border-red-500/50 text-red-400 hover:bg-red-500/20" 
                      : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                    }
                  >
                    {selectedUser.is_active !== false ? (
                      <><Ban className="h-4 w-4 mr-1" /> Disable User</>
                    ) : (
                      <><UserCheck className="h-4 w-4 mr-1" /> Enable User</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetPassword}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                  >
                    <KeyRound className="h-4 w-4 mr-1" /> Reset Password
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setUserDetailDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveUser}
                    disabled={savingUser}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingUser ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Impersonation Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-400" />
              Start Impersonation Session
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View the tenant&apos;s application as a read-only session
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStartImpersonation} className="space-y-4 mt-4">
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-400">Security Notice</p>
                  <p className="text-sm text-slate-300 mt-1">
                    This action will be logged in the audit trail. You must provide a reason and ticket ID.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Tenant ID</Label>
              <Input
                value={impersonateForm.tenant_id}
                disabled
                className="bg-slate-700 border-slate-600 text-slate-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Ticket ID *</Label>
              <Input
                value={impersonateForm.ticket_id}
                onChange={(e) => setImpersonateForm({ ...impersonateForm, ticket_id: e.target.value })}
                placeholder="TICKET-12345"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason *</Label>
              <Textarea
                value={impersonateForm.reason}
                onChange={(e) => setImpersonateForm({ ...impersonateForm, reason: e.target.value })}
                placeholder="Describe the support issue..."
                className="bg-slate-700 border-slate-600"
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Session Duration</Label>
              <Select value={String(impersonateForm.duration)} onValueChange={(v) => setImpersonateForm({ ...impersonateForm, duration: Number(v) })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setImpersonateDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700 gap-2" disabled={impersonating}>
                {impersonating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                Start Impersonation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Subscription Management Dialog */}
      <Dialog open={subscriptionDialog} onOpenChange={setSubscriptionDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              {subscriptionForm.action === 'change_plan' && 'Change Plan'}
              {subscriptionForm.action === 'extend_trial' && 'Extend Trial'}
              {subscriptionForm.action === 'apply_credit' && 'Apply Credit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {subscriptionForm.action === 'change_plan' && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-300">New Plan</Label>
                  <Select value={subscriptionForm.plan} onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, plan: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="expert">Expert</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Reason</Label>
                  <Textarea
                    value={subscriptionForm.reason}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, reason: e.target.value })}
                    placeholder="Reason for plan change..."
                    className="bg-slate-700 border-slate-600"
                    rows={2}
                  />
                </div>
              </>
            )}
            
            {subscriptionForm.action === 'extend_trial' && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-300">Extend by (days)</Label>
                  <Input
                    type="number"
                    value={subscriptionForm.days}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, days: Number(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                    min={1}
                    max={90}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Reason</Label>
                  <Textarea
                    value={subscriptionForm.reason}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, reason: e.target.value })}
                    placeholder="Reason for extending trial..."
                    className="bg-slate-700 border-slate-600"
                    rows={2}
                  />
                </div>
              </>
            )}
            
            {subscriptionForm.action === 'apply_credit' && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-300">Credit Amount ($)</Label>
                  <Input
                    type="number"
                    value={subscriptionForm.amount}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, amount: Number(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                    min={0}
                    step={0.01}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Reason *</Label>
                  <Textarea
                    value={subscriptionForm.reason}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, reason: e.target.value })}
                    placeholder="Reason for credit..."
                    className="bg-slate-700 border-slate-600"
                    rows={2}
                    required
                  />
                </div>
              </>
            )}
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setSubscriptionDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={async () => {
                  const adminToken = localStorage.getItem('admin_token');
                  localStorage.setItem('token', adminToken);
                  try {
                    if (subscriptionForm.action === 'change_plan') {
                      await adminAPI.updateTenantPlan(subscriptionForm.tenant_id, { plan: subscriptionForm.plan, reason: subscriptionForm.reason });
                      toast.success('Plan updated');
                    } else if (subscriptionForm.action === 'extend_trial') {
                      await adminAPI.extendTrial(subscriptionForm.tenant_id, { days: subscriptionForm.days, reason: subscriptionForm.reason });
                      toast.success(`Trial extended by ${subscriptionForm.days} days`);
                    } else if (subscriptionForm.action === 'apply_credit') {
                      await adminAPI.applyCredit(subscriptionForm.tenant_id, { amount: subscriptionForm.amount, reason: subscriptionForm.reason });
                      toast.success(`Credit of $${subscriptionForm.amount} applied`);
                    }
                    setSubscriptionDialog(false);
                    loadData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Operation failed');
                  }
                }}
              >
                Confirm
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* OAuth Credentials Dialog */}
      <Dialog open={oauthDialog} onOpenChange={setOAuthDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-400" />
              {oauthForm.provider === 'google' ? 'Google' : 'Microsoft'} OAuth Credentials
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update OAuth credentials for {oauthForm.provider === 'google' ? 'Gmail & Google Calendar' : 'Outlook & Microsoft 365'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-400">Security Notice</p>
                  <p className="text-sm text-slate-300 mt-1">
                    Changes will be logged in the audit trail. Existing tenant connections may need to be re-authorized.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Client ID *</Label>
              <Input
                value={oauthForm.client_id}
                onChange={(e) => setOAuthForm({ ...oauthForm, client_id: e.target.value })}
                placeholder={oauthForm.provider === 'google' ? 'xxxxx.apps.googleusercontent.com' : 'Application (client) ID'}
                className="bg-slate-700 border-slate-600 font-mono text-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Client Secret *</Label>
              <Input
                type="password"
                value={oauthForm.client_secret}
                onChange={(e) => setOAuthForm({ ...oauthForm, client_secret: e.target.value })}
                placeholder="Enter new secret (leave blank to keep existing)"
                className="bg-slate-700 border-slate-600"
              />
              <p className="text-xs text-slate-500">Leave blank to keep existing secret</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Redirect URI (optional)</Label>
              <Input
                value={oauthForm.redirect_uri}
                onChange={(e) => setOAuthForm({ ...oauthForm, redirect_uri: e.target.value })}
                placeholder="Custom redirect URI"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setOAuthDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                className="bg-amber-600 hover:bg-amber-700 gap-2"
                disabled={savingOAuth || !oauthForm.client_id}
                onClick={async () => {
                  setSavingOAuth(true);
                  const adminToken = localStorage.getItem('admin_token');
                  localStorage.setItem('token', adminToken);
                  try {
                    const updateData = { client_id: oauthForm.client_id };
                    if (oauthForm.client_secret) {
                      updateData.client_secret = oauthForm.client_secret;
                    }
                    if (oauthForm.redirect_uri) {
                      updateData.redirect_uri = oauthForm.redirect_uri;
                    }
                    await adminAPI.updateOAuthCredentials(oauthForm.provider, updateData);
                    toast.success(`${oauthForm.provider} credentials updated`);
                    setOAuthDialog(false);
                    loadData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to update credentials');
                  } finally {
                    setSavingOAuth(false);
                  }
                }}
              >
                {savingOAuth ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                Save Credentials
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Write Mode Upgrade Dialog */}
      <Dialog open={writeModeDialog} onOpenChange={setWriteModeDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Enable Write Mode
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upgrade impersonation to full write access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-400">⚠️ DANGER ZONE</p>
                  <p className="text-sm text-slate-300 mt-1">
                    Write mode allows you to make changes to the tenant&apos;s data. All actions will be permanently logged. 
                    Use only when absolutely necessary.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Your Password *</Label>
              <Input
                type="password"
                value={writeModeForm.confirmation_password}
                onChange={(e) => setWriteModeForm({ ...writeModeForm, confirmation_password: e.target.value })}
                placeholder="Confirm your password"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Ticket ID *</Label>
              <Input
                value={writeModeForm.ticket_id}
                onChange={(e) => setWriteModeForm({ ...writeModeForm, ticket_id: e.target.value })}
                placeholder="TICKET-12345"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason *</Label>
              <Textarea
                value={writeModeForm.reason}
                onChange={(e) => setWriteModeForm({ ...writeModeForm, reason: e.target.value })}
                placeholder="Explain why write access is required..."
                className="bg-slate-700 border-slate-600"
                rows={3}
                required
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setWriteModeDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 gap-2"
                disabled={upgradingWriteMode || !writeModeForm.confirmation_password || !writeModeForm.ticket_id || !writeModeForm.reason}
                onClick={async () => {
                  setUpgradingWriteMode(true);
                  const adminToken = localStorage.getItem('admin_token');
                  localStorage.setItem('token', adminToken);
                  try {
                    const response = await adminAPI.upgradeImpersonation({
                      session_id: impersonationSession?.session_id,
                      confirmation_password: writeModeForm.confirmation_password,
                      reason: writeModeForm.reason,
                      ticket_id: writeModeForm.ticket_id
                    });
                    
                    // Update impersonation session
                    const updatedSession = {
                      ...impersonationSession,
                      access_level: 'write',
                      token: response.data.token
                    };
                    localStorage.setItem('impersonation_session', JSON.stringify(updatedSession));
                    setImpersonationSession(updatedSession);
                    
                    toast.success('Write mode enabled - use with extreme caution');
                    setWriteModeDialog(false);
                    setWriteModeForm({ session_id: '', confirmation_password: '', reason: '', ticket_id: '' });
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to enable write mode');
                  } finally {
                    setUpgradingWriteMode(false);
                  }
                }}
              >
                {upgradingWriteMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                Enable Write Mode
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== BILLING CONFIG SECTION (Combined Pricing + Promo Codes) ====================
const BillingConfigSection = () => {
  const [activeTab, setActiveTab] = useState('pricing');
  
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-800/50 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('pricing')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'pricing' 
              ? 'bg-blue-600 text-white' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'
          }`}
        >
          <DollarSign className="h-4 w-4 inline mr-2" />
          Pricing & Plans
        </button>
        <button
          onClick={() => setActiveTab('promo')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'promo' 
              ? 'bg-blue-600 text-white' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'
          }`}
        >
          <Ticket className="h-4 w-4 inline mr-2" />
          Promo Codes
        </button>
      </div>
      
      {activeTab === 'pricing' && <PricingSection />}
      {activeTab === 'promo' && <PromoCodesSection />}
    </div>
  );
};

// ==================== PRICING SECTION (ENHANCED) ====================
const PricingSection = () => {
  const [subscriptionTiers, setSubscriptionTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTiers, setEditedTiers] = useState({});
  const [expandedTier, setExpandedTier] = useState(null);

  // All available features for selection
  const allFeatures = [
    { id: 'dashboard', label: 'Dashboard', category: 'Core' },
    { id: 'clients', label: 'Clients', category: 'Core' },
    { id: 'cases', label: 'Cases', category: 'Core' },
    { id: 'conflict_check', label: 'Conflict Check', category: 'Core' },
    { id: 'documents', label: 'Documents', category: 'Core' },
    { id: 'forms', label: 'Forms', category: 'Core' },
    { id: 'emails', label: 'Emails', category: 'Communication' },
    { id: 'tasks', label: 'Tasks', category: 'Core' },
    { id: 'calendar', label: 'Calendar', category: 'Core' },
    { id: 'time_tracking', label: 'Time Tracking', category: 'Billing' },
    { id: 'invoicing', label: 'Invoicing', category: 'Billing' },
    { id: 'trust_accounting', label: 'Trust Accounting', category: 'Billing' },
    { id: 'esignature', label: 'E-Signature', category: 'Core' },
    { id: 'reports', label: 'Reports', category: 'Advanced' },
    { id: 'advanced_reports', label: 'Advanced Reports', category: 'Advanced' },
    { id: 'automations', label: 'Automations', category: 'Advanced' },
    { id: 'ai_assistant', label: 'AI Assistant', category: 'Advanced' },
    { id: 'leads', label: 'Leads', category: 'Advanced' },
    { id: 'expenses', label: 'Expenses', category: 'Billing' },
    { id: 'team_chat', label: 'Team Chat', category: 'Communication' },
    { id: 'case_intelligence', label: 'Case Intelligence', category: 'Advanced' },
    { id: 'custom_pricing', label: 'Custom Pricing', category: 'Enterprise' },
    { id: 'advanced_permissions', label: 'Advanced Permissions', category: 'Enterprise' },
    { id: 'dedicated_support', label: 'Dedicated Support', category: 'Enterprise' },
    { id: 'priority_support', label: 'Priority Support', category: 'Enterprise' },
    { id: 'custom_integrations', label: 'Custom Integrations', category: 'Enterprise' },
    { id: 'sso', label: 'SSO', category: 'Enterprise' },
    { id: 'api_access', label: 'API Access', category: 'Enterprise' },
  ];

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      const res = await adminAPI.getSubscriptionTiers();
      setSubscriptionTiers(res.data?.tiers || {});
      setEditedTiers(res.data?.tiers || {});
    } catch (error) {
      console.error('Failed to load subscription tiers:', error);
      toast.error('Failed to load subscription tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      await adminAPI.updateSubscriptionTiers(editedTiers);
      toast.success('Subscription tiers updated! Changes apply to new subscriptions.');
      setEditMode(false);
      loadTiers();
    } catch (error) {
      toast.error('Failed to update subscription tiers');
    } finally {
      setSaving(false);
    }
  };

  const updateTierField = (tierKey, field, value) => {
    setEditedTiers({
      ...editedTiers,
      [tierKey]: { ...editedTiers[tierKey], [field]: value }
    });
  };

  const toggleFeature = (tierKey, featureId) => {
    const tier = editedTiers[tierKey];
    const currentFeatures = Array.isArray(tier.features) ? tier.features : [];
    const newFeatures = currentFeatures.includes(featureId)
      ? currentFeatures.filter(f => f !== featureId)
      : [...currentFeatures, featureId];
    updateTierField(tierKey, 'features', newFeatures);
  };

  const tierColors = {
    free: 'border-slate-500',
    trial: 'border-amber-500',
    professional: 'border-blue-500',
    expert: 'border-purple-500',
    enterprise: 'border-emerald-500'
  };

  const tierBadgeColors = {
    free: 'bg-slate-500/20 text-slate-300',
    trial: 'bg-amber-500/20 text-amber-300',
    professional: 'bg-blue-500/20 text-blue-300',
    expert: 'bg-purple-500/20 text-purple-300',
    enterprise: 'bg-emerald-500/20 text-emerald-300'
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-400" />
              Subscription Plans Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Admin Console is the single source of truth. Changes apply to new subscriptions immediately.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditedTiers(subscriptionTiers); }}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save Changes</>}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => setEditMode(true)} className="bg-blue-600 hover:bg-blue-700">
                <PenLine className="h-4 w-4 mr-2" /> Edit Plans
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {['trial', 'professional', 'expert', 'enterprise'].map((tierKey) => {
          const tier = editedTiers[tierKey];
          if (!tier) return null;
          const isExpanded = expandedTier === tierKey;

          return (
            <Card 
              key={tierKey} 
              className={`bg-slate-800/50 border-2 ${tierColors[tierKey]} transition-all ${isExpanded ? 'col-span-full' : ''}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={tierBadgeColors[tierKey]}>{tier.name}</Badge>
                    {tierKey === 'expert' && <Badge className="bg-purple-600 text-white text-xs">Popular</Badge>}
                    {tier.is_admin_only && <Badge className="bg-red-500/20 text-red-300 text-xs">Admin Only</Badge>}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setExpandedTier(isExpanded ? null : tierKey)}
                    className="text-slate-400 hover:text-white"
                  >
                    {isExpanded ? <X className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
                
                {/* Description */}
                {editMode ? (
                  <Input
                    value={tier.description || ''}
                    onChange={(e) => updateTierField(tierKey, 'description', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-sm mt-2"
                    placeholder="Plan description"
                  />
                ) : (
                  <p className="text-xs text-slate-400 mt-1">{tier.description}</p>
                )}
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Pricing */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Monthly Price</Label>
                    {editMode && !tier.is_custom_quote ? (
                      <div className="flex items-center">
                        <span className="text-slate-400 mr-1">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={tier.monthly_price || ''}
                          onChange={(e) => updateTierField(tierKey, 'monthly_price', parseFloat(e.target.value) || 0)}
                          className="bg-slate-700 border-slate-600 h-8"
                        />
                      </div>
                    ) : (
                      <p className="text-xl font-bold text-white">
                        {tier.is_custom_quote ? 'Custom' : `$${tier.monthly_price?.toFixed(2) || '0.00'}`}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Annual Discount</Label>
                    {editMode ? (
                      <div className="flex items-center">
                        <Input
                          type="number"
                          value={tier.annual_discount || ''}
                          onChange={(e) => updateTierField(tierKey, 'annual_discount', parseInt(e.target.value) || 0)}
                          className="bg-slate-700 border-slate-600 h-8 w-16"
                        />
                        <span className="text-slate-400 ml-1">%</span>
                      </div>
                    ) : (
                      <p className="text-lg font-semibold text-emerald-400">{tier.annual_discount || 0}% off</p>
                    )}
                  </div>
                </div>

                {/* Features Section */}
                {isExpanded && (
                  <div className="border-t border-slate-700 pt-4 mt-4">
                    <Label className="text-slate-300 text-sm font-medium mb-3 block">Included Features</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {allFeatures.map((feature) => {
                        const isIncluded = Array.isArray(tier.features) && tier.features.includes(feature.id);
                        return (
                          <div 
                            key={feature.id}
                            onClick={() => editMode && toggleFeature(tierKey, feature.id)}
                            className={`p-2 rounded text-xs flex items-center gap-2 transition-colors ${
                              editMode ? 'cursor-pointer hover:bg-slate-600' : ''
                            } ${isIncluded ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700/50 text-slate-500'}`}
                          >
                            {isIncluded ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            {feature.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick Feature Count */}
                {!isExpanded && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Check className="h-4 w-4 text-emerald-400" />
                    {Array.isArray(tier.features) ? tier.features.length : 0} features included
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Admin-Only Free Plan */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-400">Free Plan (Admin-Only)</p>
                <p className="text-xs text-slate-500">For demos, internal testing, or special cases. Only admins can assign.</p>
              </div>
            </div>
            <Badge className="bg-slate-600 text-slate-300">Cannot be self-selected</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PROMO CODES SECTION ====================
const PromoCodesSection = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    applies_to_plans: ['all'],
    applies_to_billing: 'both',
    duration: 'one_time',
    max_uses: null,
    expires_at: '',
    description: ''
  });

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      const res = await adminAPI.listPromoCodes();
      setPromoCodes(res.data || []);
    } catch (error) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      await adminAPI.createPromoCode({
        ...newPromo,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        expires_at: newPromo.expires_at || null
      });
      toast.success('Promo code created');
      setShowCreateDialog(false);
      setNewPromo({ code: '', discount_type: 'percentage', discount_value: 10, applies_to_plans: ['all'], applies_to_billing: 'both', duration: 'one_time', max_uses: null, expires_at: '', description: '' });
      loadPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (promo) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      await adminAPI.updatePromoCode(promo.promo_id, { is_active: !promo.is_active });
      toast.success(`Promo code ${promo.is_active ? 'deactivated' : 'activated'}`);
      loadPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Ticket className="h-5 w-5 text-amber-400" />
              Promo Codes
            </CardTitle>
            <CardDescription className="text-slate-400">
              Manage discount codes and promotions
            </CardDescription>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Create Code
          </Button>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No promo codes yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Code</TableHead>
                  <TableHead className="text-slate-300">Discount</TableHead>
                  <TableHead className="text-slate-300">Duration</TableHead>
                  <TableHead className="text-slate-300">Uses</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map(promo => (
                  <TableRow key={promo.promo_id} className="border-slate-700">
                    <TableCell className="font-mono text-white">{promo.code}</TableCell>
                    <TableCell className="text-emerald-400">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{promo.duration.replace('_', ' ')}</TableCell>
                    <TableCell className="text-slate-300">{promo.current_uses || 0}{promo.max_uses ? `/${promo.max_uses}` : ''}</TableCell>
                    <TableCell>
                      <Badge className={promo.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={promo.is_active} onCheckedChange={() => handleToggle(promo)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} placeholder="SAVE20" className="bg-slate-700 border-slate-600" required />
              </div>
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={newPromo.discount_type} onValueChange={(v) => setNewPromo({ ...newPromo, discount_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Value *</Label>
                <Input type="number" value={newPromo.discount_value} onChange={(e) => setNewPromo({ ...newPromo, discount_value: parseFloat(e.target.value) })} className="bg-slate-700 border-slate-600" required />
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={newPromo.duration} onValueChange={(v) => setNewPromo({ ...newPromo, duration: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One Time</SelectItem>
                    <SelectItem value="3_months">3 Months</SelectItem>
                    <SelectItem value="6_months">6 Months</SelectItem>
                    <SelectItem value="12_months">12 Months</SelectItem>
                    <SelectItem value="lifetime">Lifetime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Uses (optional)</Label>
                <Input type="number" value={newPromo.max_uses || ''} onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })} placeholder="Unlimited" className="bg-slate-700 border-slate-600" />
              </div>
              <div className="space-y-2">
                <Label>Expires (optional)</Label>
                <Input type="date" value={newPromo.expires_at} onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={newPromo.description} onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })} placeholder="Holiday discount..." className="bg-slate-700 border-slate-600" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-blue-600 hover:bg-blue-700">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ROLES SECTION ====================
const RolesSection = () => {
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedRoles, setEditedRoles] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      const [rolesRes, permsRes] = await Promise.all([
        adminAPI.getRoles(),
        adminAPI.getPermissions()
      ]);
      setRoles(rolesRes.data.roles || {});
      setEditedRoles(rolesRes.data.roles || {});
      setPermissions(permsRes.data.permissions || []);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const adminToken = localStorage.getItem('admin_token');
      localStorage.setItem('token', adminToken);
      await adminAPI.updateRoles({ roles: editedRoles });
      toast.success('Roles updated successfully');
      setEditMode(false);
      loadRoles();
    } catch (error) {
      toast.error('Failed to update roles');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (roleName, permission) => {
    const role = editedRoles[roleName];
    const perms = role.permissions || [];
    const hasPermission = perms.includes(permission);
    
    setEditedRoles({
      ...editedRoles,
      [roleName]: {
        ...role,
        permissions: hasPermission 
          ? perms.filter(p => p !== permission)
          : [...perms, permission]
      }
    });
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>;

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    const cat = perm.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Lock className="h-5 w-5 text-purple-400" />
              Roles & Permissions
            </CardTitle>
            <CardDescription className="text-slate-400">
              Client app roles configuration (Source of Truth)
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => { setEditMode(false); setEditedRoles(roles); }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditMode(true)} className="bg-blue-600 hover:bg-blue-700">
                <PenLine className="h-4 w-4 mr-2" /> Edit Roles
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(editedRoles).map(([roleName, role]) => (
              <div key={roleName} className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-white capitalize">{roleName}</h3>
                    <p className="text-sm text-slate-400">{role.description}</p>
                  </div>
                  {role.can_be_assigned === false && (
                    <Badge className="bg-amber-500/20 text-amber-300">System Role</Badge>
                  )}
                </div>
                
                {editMode && role.can_be_assigned !== false && (
                  <div className="mt-4 space-y-4">
                    {Object.entries(permissionsByCategory).map(([category, perms]) => (
                      <div key={category}>
                        <h4 className="text-xs font-medium text-slate-400 uppercase mb-2">{category}</h4>
                        <div className="flex flex-wrap gap-2">
                          {perms.map(perm => (
                            <button
                              key={perm.key}
                              onClick={() => togglePermission(roleName, perm.key)}
                              className={cn(
                                "px-3 py-1 text-xs rounded-full border transition-colors",
                                (role.permissions || []).includes(perm.key) || (role.permissions || []).includes('*')
                                  ? "bg-blue-500/20 border-blue-500 text-blue-300"
                                  : "bg-slate-600/50 border-slate-500 text-slate-400 hover:border-slate-400"
                              )}
                            >
                              {perm.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {!editMode && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(role.permissions || []).slice(0, 8).map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs border-slate-500 text-slate-300">
                        {perm}
                      </Badge>
                    ))}
                    {(role.permissions || []).length > 8 && (
                      <Badge variant="outline" className="text-xs border-slate-500 text-slate-400">
                        +{role.permissions.length - 8} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminConsole;
